import React, { useEffect, useMemo, useRef } from 'react';

/**
 * VisitAssignmentsSlip — Phase 1f.
 *
 * Browser-printable HTML slip for the receptionist: "who's doing what
 * on this visit." Renders into a hidden iframe, triggers the native
 * print dialog, then cleans up. A5 primary; 80mm fallback for thermal
 * setups via @media print.
 *
 * Patient identity row carries name + mobile + DOB + queue # — common-name
 * disambiguation matters in Indian OPD reception desks.
 *
 * Props:
 *   visit        — { visit_id, patient_name, patient_mobile, patient_dob,
 *                    queue_number, doctor_name, visit_date }
 *   sittings     — list of sittings on the visit (from byVisit projection)
 *   clinicName   — org's clinic name for the header (optional)
 *   onPrinted    — optional callback once print dialog dispatched
 *   onClose      — optional callback when user dismisses
 */
export default function VisitAssignmentsSlip({
  visit,
  sittings = [],
  clinicName,
  onPrinted,
  onClose,
}) {
  const iframeRef = useRef(null);

  const html = useMemo(() => renderSlipHtml({
    visit,
    sittings,
    clinicName,
  }), [visit, sittings, clinicName]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return undefined;
    doc.open();
    doc.write(html);
    doc.close();
    // Wait a tick for the iframe to layout, then print.
    const t = setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        if (onPrinted) onPrinted();
      } catch (err) {
        console.warn('[VisitAssignmentsSlip] print failed:', err);
      }
    }, 80);
    return () => clearTimeout(t);
  }, [html, onPrinted]);

  // The iframe is off-screen; the slip shows in the native print dialog.
  // Overlay adds a small "Close" handle so the user can dismiss the
  // surrounding modal without dodging the browser's print UI.
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5">
        <h2 className="text-base font-semibold mb-2">Print assignments slip</h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
          Your browser's print dialog should have opened. If not, click Retry.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => iframeRef.current?.contentWindow?.print()}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Retry print
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-sm rounded-md bg-teal-600 hover:bg-teal-700 text-white"
          >
            Close
          </button>
        </div>
        <iframe
          ref={iframeRef}
          title="Visit assignments slip"
          style={{
            position: 'fixed', left: '-10000px', top: 0, width: 0, height: 0,
            border: 0,
          }}
        />
      </div>
    </div>
  );
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatCurrency(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatDob(value) {
  if (!value) return '';
  // value may be ISO date or already 'DD-MMM-YYYY'; render as-is for the
  // MVP — receptionist reads aloud, the raw string is fine.
  return String(value);
}

function renderSlipHtml({ visit, sittings, clinicName }) {
  const patient = escapeHtml(visit?.patient_name || 'Patient');
  const mobile = escapeHtml(visit?.patient_mobile || '—');
  const dob = escapeHtml(formatDob(visit?.patient_dob || visit?.dob) || '—');
  const queueNo = visit?.queue_number != null ? `#Q-${escapeHtml(visit.queue_number)}` : '';
  const doctor = escapeHtml(visit?.doctor_name || '—');
  const date = escapeHtml(visit?.visit_date || new Date().toISOString().slice(0, 10));
  const clinic = escapeHtml(clinicName || '');
  const now = new Date();
  const printedAt = escapeHtml(now.toLocaleString('en-IN'));

  const rows = sittings.map((s) => `
    <tr>
      <td>${escapeHtml(s.procedure_name || '(procedure)')}</td>
      <td>${escapeHtml(s.assigned_to_name || s.assigned_to_role || '—')}</td>
      <td>${escapeHtml(String(s.status || '').replace('_', ' '))}</td>
      <td class="num">${escapeHtml(formatCurrency(s.final_cost))}</td>
    </tr>
  `).join('');

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Visit assignments</title>
    <style>
      @page { size: A5 portrait; margin: 10mm; }
      * { box-sizing: border-box; }
      body {
        font-family: 'Inter', system-ui, -apple-system, sans-serif;
        color: #0f172a;
        font-size: 12px;
        margin: 0;
        padding: 0;
      }
      h1 { font-size: 14px; margin: 0 0 2px 0; }
      .muted { color: #64748b; font-size: 10px; }
      .card {
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        padding: 8px 10px;
        margin-top: 8px;
      }
      .row { display: flex; justify-content: space-between; gap: 8px; }
      table { width: 100%; border-collapse: collapse; margin-top: 8px; }
      th, td { text-align: left; padding: 4px 6px; border-bottom: 1px solid #e2e8f0; font-size: 11px; }
      th { background: #f8fafc; font-weight: 600; }
      td.num { text-align: right; font-variant-numeric: tabular-nums; }
      .footer { margin-top: 10px; padding-top: 6px; border-top: 1px dashed #cbd5e1; font-size: 10px; color: #64748b; }
      @media print and (max-width: 80mm) {
        @page { size: 80mm auto; margin: 3mm; }
        body { font-size: 10px; }
        h1 { font-size: 12px; }
        th, td { padding: 3px 2px; font-size: 9px; }
      }
    </style>
  </head>
  <body>
    <h1>${clinic || 'Visit assignments'}</h1>
    <div class="muted">Printed ${printedAt}</div>

    <div class="card">
      <div class="row">
        <div>
          <div style="font-weight:600; font-size:13px;">${patient}</div>
          <div class="muted">Mobile: ${mobile} · DOB: ${dob}</div>
        </div>
        <div style="text-align:right;">
          <div style="font-weight:600;">${queueNo}</div>
          <div class="muted">${date}</div>
        </div>
      </div>
      <div class="muted" style="margin-top:4px;">Primary doctor: ${doctor}</div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Procedure</th>
          <th>Assigned to</th>
          <th>Status</th>
          <th class="num">Cost</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="4" class="muted">No procedures on this visit.</td></tr>'}
      </tbody>
    </table>

    <div class="footer">
      Please follow the sequence above. Retain this slip for reference.
    </div>
  </body>
</html>`;
}
